// ============================================================================
// ACTIVITY TEXT - Traducción del log de actividad a texto humano (español)
// ============================================================================
// El backend guarda cada mutación en `actividad_log` con un `accion` técnico y
// los campos `datos_anteriores`/`datos_nuevos` (solo el campo relevante). Aquí
// se traduce cada `accion` a una frase legible para las pestañas Actividad/Todo
// del detalle de tarea.
//
// describeActivity() NO devuelve JSX (para mantener el util puro y testeable):
// devuelve { iconKey, tone, segments }, donde `segments` es una lista de
// { text, strong } que el componente pinta (los `strong` en negrita, como el
// mockup: "cambió el estado a **En curso**").
// ============================================================================
import { parseServerDate } from './serverDate';

// Etiquetas de estado (el valor crudo de la BBDD → etiqueta de UI).
export const ESTADO_LABELS = {
  pendiente: 'Pendiente',
  'en-curso': 'En curso',
  en_curso: 'En curso',
  completada: 'Completada',
  reabierta: 'Reabierta',
};

export function formatEstado(value) {
  if (value === null || value === undefined || value === '') return '—';
  return ESTADO_LABELS[value] || String(value);
}

/**
 * Traduce una fila de actividad a algo legible.
 *
 * @param {object} item     Fila del log: { accion, datos_anteriores, datos_nuevos, ... }
 * @param {object} helpers  { resolveSprintName(id), resolveUserName(id) } opcionales.
 * @returns {{ iconKey:string, tone:string, segments:Array<{text:string, strong?:boolean}> }}
 */
export function describeActivity(item, helpers = {}) {
  const resolveSprintName = helpers.resolveSprintName || ((id) => id || 'otro sprint');
  const resolveUserName = helpers.resolveUserName || ((id) => `usuario #${id}`);
  const next = item?.datos_nuevos || {};

  switch (item?.accion) {
    case 'creada':
      return { iconKey: 'created', tone: 'accent', segments: [{ text: 'creó la tarea' }] };

    case 'eliminada':
      return { iconKey: 'deleted', tone: 'danger', segments: [{ text: 'eliminó la tarea' }] };

    case 'estado_cambiado':
      return {
        iconKey: 'status',
        tone: 'default',
        segments: [
          { text: 'cambió el estado a ' },
          { text: formatEstado(next.estado), strong: true },
        ],
      };

    case 'validado_seo':
      return {
        iconKey: 'validated',
        tone: 'success',
        segments: [{ text: 'validó la tarea por SEO' }],
      };

    case 'desvalidado_seo':
      return {
        iconKey: 'unvalidated',
        tone: 'danger',
        segments: [{ text: 'retiró la validación por SEO' }],
      };

    case 'asignada': {
      const newId = next.assigned_to;
      if (newId === null || newId === undefined) {
        return {
          iconKey: 'assigned',
          tone: 'default',
          segments: [{ text: 'quitó la persona asignada' }],
        };
      }
      return {
        iconKey: 'assigned',
        tone: 'default',
        segments: [
          { text: 'asignó la tarea a ' },
          { text: resolveUserName(newId), strong: true },
        ],
      };
    }

    case 'bloqueada':
      return { iconKey: 'blocked', tone: 'danger', segments: [{ text: 'bloqueó la tarea' }] };

    case 'desbloqueada':
      return {
        iconKey: 'unblocked',
        tone: 'success',
        segments: [{ text: 'desbloqueó la tarea' }],
      };

    case 'movida_sprint': {
      const newId = next.sprint_actual;
      if (newId === null || newId === undefined) {
        return {
          iconKey: 'moved',
          tone: 'default',
          segments: [{ text: 'movió la tarea al ' }, { text: 'backlog', strong: true }],
        };
      }
      return {
        iconKey: 'moved',
        tone: 'accent',
        segments: [
          { text: 'movió la tarea a ' },
          { text: resolveSprintName(newId), strong: true },
        ],
      };
    }

    default:
      // Fallback legible para acciones futuras aún no mapeadas.
      return {
        iconKey: 'generic',
        tone: 'default',
        segments: [
          { text: `registró: ${String(item?.accion || 'actividad').replace(/_/g, ' ')}` },
        ],
      };
  }
}

/**
 * Fecha compacta estilo mockup: "hoy · 10:24", "ayer · 18:03", "1 jul · 09:10",
 * "1 jul 2025 · 09:10" (si es de otro año). Tolera el formato datetime de MySQL
 * ("YYYY-MM-DD HH:MM:SS") normalizando el espacio a 'T' para Safari.
 */
export function formatEventDate(dateString) {
  if (!dateString) return '';
  const d = parseServerDate(dateString);
  if (isNaN(d.getTime())) return '';

  const now = new Date();
  const time = d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (sameDay(d, now)) return `hoy · ${time}`;

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, yesterday)) return `ayer · ${time}`;

  const day = d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  const label = d.getFullYear() !== now.getFullYear() ? `${day} ${d.getFullYear()}` : day;
  return `${label} · ${time}`;
}
